import React, { useState, useEffect } from "react";
import { FormTitle, LidHeader, PageWrapper, FormFieldWrapper, ButtonWrapper } from "./LidFormPage.styles";
import { FormData } from "./LidFormPage.types";
import DropdownWithObjects from "../../components/Dropdowns/DropdownWithObjects/DropdownWithObjects";
import Button from "../../components/Buttons/Button";
import SuccessModal from "../../components/Modals/SuccessModal/SuccessModal";
import { LeadData } from "../../services/leadService/leadService.types";
import { createLead, fetchCourses, fetchRoles, fetchTelegramApplicationLegalState, TelegramApplicationLegalDocument } from "../../services/leadService/leadService";
import { useViewportContext } from "../../context/ViewportContext";
import IOSCompatibleInput from "../../components/IOSCompatibleInput/IOSCompatibleInput";
import KeyboardDismisser from "../../components/KeyboardDismisser/KeyboardDismisser";

import { logger } from "utils/logger";
const LidFormPage: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    surname: "",
    patronymic: "",
    phone: "",
    email: "",
    role: "",
    course: "",
  });
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(true);
  const [roles, setRoles] = useState<{ id: number; name: string }[]>([]);
  const [courses, setCourses] = useState<{ id: number; title: string }[]>([]);
  const [botId, setBotId] = useState<string | null>(null);
  const [userNameFromTelegram, setUserNameFromTelegram] = useState<string | null>(null);
  const [userTgId, setUserTgId] = useState<string | null>(null);
  const [defaultCourseId, setDefaultCourseId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string>("");
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [legalDocuments, setLegalDocuments] = useState<TelegramApplicationLegalDocument[]>([]);
  const [acceptedDocumentIds, setAcceptedDocumentIds] = useState<Set<number>>(new Set());
  const [legalConfigurationComplete, setLegalConfigurationComplete] = useState(true);

  // Получение данных viewport из контекста
  const { isKeyboardVisible, keyboardHeight } = useViewportContext();
  const bottomOffset = isKeyboardVisible ? keyboardHeight + 40 : 0;


  // Расширяем WebApp при загрузке
  useEffect(() => {
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.expand();
    }
  }, []);

  useEffect(() => {
    const requiredValues = [formData.name, formData.surname, formData.phone, formData.email, formData.role, formData.course];
    const allFieldsValid = requiredValues.every((value) => value.trim() !== "");
    const requiredLegalAccepted = legalDocuments.filter((item) => item.required).every((item) => acceptedDocumentIds.has(item.id));
    setIsSubmitDisabled(!allFieldsValid || !legalConfigurationComplete || !requiredLegalAccepted);
  }, [formData, legalDocuments, acceptedDocumentIds, legalConfigurationComplete]);

  // Получение параметров из URL
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const botIdFromTelegram = queryParams.get("bot_id");
    const userNameTelegram = queryParams.get("user_name");
    const courseIdFromUrl = queryParams.get("course_id");
    const userTGIdFromUrl = queryParams.get("user_id");

    if (botIdFromTelegram) {
      setBotId(botIdFromTelegram);
    } else {
      logger.error("Bot ID not found in URL.");
      setLoadError("Не удалось определить бота. Закройте форму и откройте её из Telegram ещё раз.");
      setIsLoading(false);
    }

    if (userTGIdFromUrl) {
      setUserTgId(userTGIdFromUrl);
    }

    if (courseIdFromUrl) {
      setDefaultCourseId(courseIdFromUrl);
    }

    if (userNameTelegram) {
      setUserNameFromTelegram(userNameTelegram);
    }
  }, []);

  // Загрузка данных для выпадающих списков
  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        if (!botId) {
          logger.error("Bot ID is not available. Cannot fetch data.");
          return;
        }

        setLoadError("");
        const [fetchedRoles, fetchedCourses] = await Promise.all([
          fetchRoles(botId),
          fetchCourses({ botId }),
        ]);

        setRoles(fetchedRoles);
        setCourses(fetchedCourses);

        if (!fetchedRoles.length || !fetchedCourses.length) {
          setLoadError(
            !fetchedCourses.length
              ? "У этого преподавателя сейчас нет доступных курсов для заявки."
              : "Не удалось получить список ролей для заявки."
          );
        }

        if (defaultCourseId) {
          const defaultCourse = fetchedCourses.find(
            (course) => course.id.toString() === defaultCourseId
          );
          if (defaultCourse) {
            setFormData((prevFormData) => ({
              ...prevFormData,
              course: defaultCourse.id.toString(),
            }));
          } else if (fetchedCourses.length) {
            setLoadError("Выбранный курс недоступен для подачи заявки.");
          }
        }
      } catch (error) {
        logger.error("Ошибка при загрузке данных для выпадающих списков:", error);
        setLoadError("Не удалось загрузить курсы и роли. Закройте форму и попробуйте открыть её из Telegram ещё раз.");
      } finally {
        setIsLoading(false);
      }
    };

    if (botId) {
      loadDropdownData();
    }
  }, [botId, defaultCourseId]);

  useEffect(() => {
    if (!botId || !userTgId) return;
    let active = true;
    fetchTelegramApplicationLegalState(botId, userTgId)
      .then((state) => {
        if (!active) return;
        setLegalDocuments(state.documents || []);
        setLegalConfigurationComplete(state.configuration_complete);
        setAcceptedDocumentIds(new Set((state.documents || []).filter((item) => item.accepted).map((item) => item.id)));
      })
      .catch((error) => {
        logger.error("Ошибка загрузки юридических документов заявки:", error);
        if (active) setLoadError("Не удалось загрузить обязательные согласия для заявки.");
      });
    return () => { active = false; };
  }, [botId, userTgId]);

  const validateForm = () => {
    const requiredValues = [formData.name, formData.surname, formData.phone, formData.email, formData.role, formData.course];
    const allFieldsValid = requiredValues.every((value) => value.trim() !== "");
    const requiredLegalAccepted = legalDocuments.filter((item) => item.required).every((item) => acceptedDocumentIds.has(item.id));
    setIsSubmitDisabled(!allFieldsValid || !legalConfigurationComplete || !requiredLegalAccepted);
  };

  const handleChange = (name: string, value: string) => {
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
    validateForm();
  };

  const handleSubmit = async () => {
    const newErrors: Record<string, string | null> = {};

    if (!formData.name.trim()) newErrors.name = "Это поле обязательно";
    if (!formData.email.trim()) newErrors.email = "Это поле обязательно";
    if (!formData.phone.trim()) newErrors.phone = "Это поле обязательно";

    setErrors(newErrors);

    if (Object.values(newErrors).every((err) => !err)) {
      if (!botId) {
        logger.error("Bot ID is not available. Cannot create lead.");
        return;
      }

      const leadData: LeadData = {
        name: formData.name,
        surname: formData.surname,
        patronymic: formData.patronymic,
        accepted_document_ids: Array.from(acceptedDocumentIds),
        phone: formData.phone,
        email: formData.email,
        role: formData.role,
        course: formData.course ? Number(formData.course) : null,
        telegram: userNameFromTelegram,
        bot_id: botId,
        tg_id: userTgId,
        note: "",
      };

      try {
        await createLead(leadData);
        setShowSuccessModal(true);
        setTimeout(() => {
          if (window.Telegram?.WebApp?.close) {
            window.Telegram.WebApp.close();
          }
        }, 2000);
      } catch (error) {
        logger.error("Ошибка при создании лида:", error);
        setLoadError("Не удалось отправить заявку. Проверьте данные и попробуйте ещё раз.");
      }
    }
  };

  if (isLoading) {
    return <div>Загрузка...</div>;
  }

  if (showSuccessModal) {
    return <SuccessModal message="Заявка успешно отправлена!" onClose={() => {}} />;
  }

  return (
    <>
      {/* Компонент для закрытия клавиатуры по клику вне поля ввода */}
      <KeyboardDismisser />
      <PageWrapper bottomOffset={bottomOffset}>
        <LidHeader>LISRENT</LidHeader>
        <FormTitle>Заявка</FormTitle>
        {loadError && (
          <div style={{ width: "100%", maxWidth: 369, margin: "0 auto 16px", fontSize: 14, lineHeight: 1.4 }}>
            {loadError}
          </div>
        )}
        <FormFieldWrapper>
          <DropdownWithObjects
            options={courses.map((course) => ({
              label: course.title,
              value: course.id.toString(),
            }))}
            placeholder="Курс"
            value={formData.course}
            onChange={(value) => handleChange("course", value)}
          />
          <DropdownWithObjects
            options={roles.map((role) => ({
              label: role.name,
              value: role.id.toString(),
            }))}
            placeholder="Роль"
            value={formData.role}
            onChange={(value) => handleChange("role", value)}
          />
          <IOSCompatibleInput
            type="text"
            placeholder="Имя"
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            hasError={false}
          />
          <IOSCompatibleInput
            type="text"
            placeholder="Фамилия"
            value={formData.surname}
            onChange={(e) => handleChange("surname", e.target.value)}
            hasError={false}
          />
          <IOSCompatibleInput
            type="text"
            placeholder="Отчество"
            value={formData.patronymic}
            onChange={(e) => handleChange("patronymic", e.target.value)}
            hasError={false}
          />
          <IOSCompatibleInput
            type="text"
            placeholder="Номер телефона"
            value={formData.phone}
            onChange={(e) => handleChange("phone", e.target.value)}
            hasError={false}
          />
          <IOSCompatibleInput
            type="text"
            placeholder="Почта"
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
            hasError={false}
          />
          {legalDocuments.length > 0 && (
            <div style={{ width: "100%", fontSize: 14, lineHeight: 1.4 }}>
              {legalDocuments.map((document) => (
                <label key={document.id} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 10 }}>
                  <input
                    type="checkbox"
                    checked={acceptedDocumentIds.has(document.id)}
                    disabled={document.accepted}
                    onChange={(event) => setAcceptedDocumentIds((current) => {
                      const next = new Set(current);
                      if (event.target.checked) next.add(document.id); else next.delete(document.id);
                      return next;
                    })}
                  />
                  <span>
                    {document.url ? <a href={document.url} target="_blank" rel="noreferrer">{document.title}</a> : document.title}
                    {document.required ? " *" : ""}
                    {document.accepted ? " — уже подтверждено" : ""}
                  </span>
                </label>
              ))}
              {!legalConfigurationComplete && <div>Обязательные документы для заявки временно настроены не полностью.</div>}
            </div>
          )}
        </FormFieldWrapper>
        <ButtonWrapper>
          <Button
            type="button"
            disabled={isSubmitDisabled}
            onClick={handleSubmit}
            fullWidth
          >
            Отправить заявку
          </Button>
        </ButtonWrapper>
      </PageWrapper>
    </>
  );
};

export default LidFormPage;